/**
 * Sticky Buy Bar — Product Detail (all viewports)
 *
 * Appears at the bottom of the screen (above the mobile bottom nav on small
 * screens; flush with the bottom edge on desktop, where there is no bottom
 * nav) when the original Add to Cart button scrolls out of view. Keeps the
 * CTA reachable without scrolling back through the buy box — on tall buy
 * boxes even a 1440p viewport pushes the in-panel button out of view.
 *
 * Visibility is controlled by the parent via the `isVisible` prop, which should
 * be derived from an IntersectionObserver on the original add-to-cart button.
 */

import { ShoppingCart, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFormatPrice } from '@/hooks/useFormatPrice';

interface StickyBuyBarProps {
    /** Show the bar — true when the original Add to Cart button is NOT in viewport */
    isVisible: boolean;
    productName: string;
    /** Lowest price from tiered pricing, or null if not available */
    lowestPrice: number | null;
    onAddToCart: () => void;
    isDisabled: boolean;
    isLoading: boolean;
}

export const StickyBuyBar = ({
    isVisible,
    productName,
    lowestPrice,
    onAddToCart,
    isDisabled,
    isLoading,
}: StickyBuyBarProps) => {
    const formatPrice = useFormatPrice();

    return (
        <div
            aria-hidden={!isVisible}
            className={cn(
                'fixed bottom-16 inset-x-0 z-30 lg:bottom-0',
                'bg-white border-t border-border shadow-lg',
                'transition-transform duration-200 ease-in-out',
                isVisible ? 'translate-y-0' : 'translate-y-full'
            )}
        >
            <div className="flex items-center gap-3 px-4 py-3">
                {/* Product name + price */}
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                        {productName}
                    </p>
                    {lowestPrice != null && (
                        <p className="text-xs text-muted-foreground">
                            From {formatPrice(lowestPrice)}/day
                        </p>
                    )}
                </div>

                {/* Add to Cart button */}
                <Button
                    size="sm"
                    className="shrink-0 gap-1.5"
                    onClick={onAddToCart}
                    disabled={isDisabled || isLoading}
                    aria-label={`Add ${productName} to cart`}
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <ShoppingCart className="h-4 w-4" />
                    )}
                    Add to Cart
                </Button>
            </div>
        </div>
    );
};

export default StickyBuyBar;
